import { useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box } from "@caspeco/casper-ui-library.components.box";
import { SkeletonMenu } from "components/skeletons/SkeletonMenu";
import { useDisclosure } from "@caspeco/casper-ui-library.components.modal";
import ShowCartButton from "components/cart/subcomponents/ShowCartButton";
import { ThemeSpaceVariable } from "@caspeco/casper-ui-library.base-ui.theme";
import { useMenuRouteRequests } from "routes/menu-route/hooks/useMenuRouteRequests";
import { getLocalStorage } from "helpers/local-storage-helper/local-storage-helper";
import { IAppGeneralFeatures } from "types/appFeatures";
import { usePickSearchParams } from "hooks/usePickSearchParams";
import { useLogDeliveryTypeDiff } from "hooks/useLogDeliveryTypeDiff";
import { useCookieModalStore } from "store/cookieModalStore";
import { useCartAPI, useCartStore } from "store/cartStore";
import { AbortAction, ApiService } from "services/Api.service";
import { usePlaceStore } from "store/placeStore";
import { useMenuStore } from "store/menuStore";
import { useArticleAPI } from "store/articleStore";
import { useToast } from "@caspeco/casper-ui-library.components.toast";
import { logDebug, logError } from "helpers/log-helper/log-helper";
import { createBSTLSearchParams } from "helpers/search-param-helper/search-param-helper";
import { DeliveryType, IChangeDeliveryTypeRequest } from "types/deliveryOption";
import { LogPrefix } from "types/logging";
import { PriceIncludingVat } from "types/price";
import { BSTLApiError } from "types/api-error";
import ArticleModal from "components/article-modal/ArticleModal";
import { Upsell } from "components/upsell/Upsell";
import NotFound from "components/not-found/NotFound";
import DeliveryOptionsModal from "components/delivery-options-modal/DeliveryOptionsModal";
import Cart from "components/cart/Cart";
import { MenuSectionTabs } from "../menu-section-tabs/MenuSectionTabs";
import { MenuSectionList } from "../menu-section-list/MenuSectionList";
import { StyledMenu } from "./Menu.styles";

function MenuPage() {
	const { placeId, appAlias } = useParams<"placeId" | "appAlias">();
	const [, setSearchParams] = useSearchParams();
	const supportsMenuListFeature = getLocalStorage<IAppGeneralFeatures>("appFeatures")?.showMenuSectionList;
	const navigate = useNavigate();
	const { deliveryOptionType, showMenuSectionList } = usePickSearchParams([
		"deliveryOptionType",
		"showMenuSectionList",
	]);
	const { t } = useTranslation();
	const { menu } = useMenuStore();
	const { place } = usePlaceStore();
	const { isCookieModalOpen } = useCookieModalStore();
	const { getArticle, getUpsellGroups } = useArticleAPI();
	const { cart } = useCartStore();
	const { setCartDeliveryType, setIsCartModalOpen } = useCartAPI();
	const { isLoading, error } = useMenuRouteRequests();
	const { showToast } = useToast();

	const isShowingMenuSectionList = supportsMenuListFeature && showMenuSectionList;

	useLogDeliveryTypeDiff();

	const {
		onClose: deliveryOptionsModalOnClose,
		isOpen: deliveryOptionsModalIsOpen,
		onOpen: deliveryOptionsModalOnOpen,
	} = useDisclosure();

	const { onClose: upsellOnClose, isOpen: upsellIsOpen, onOpen: upsellOnOpen } = useDisclosure();

	// Note that an article can be "opened" from both menu article listning, cart and within upsell
	const {
		onClose: articleModalOnClose,
		isOpen: articleModalIsOpen,
		onOpen: articleModalOnOpen,
	} = useDisclosure({
		onClose: () => {
			// Abort any ongoing requests for another article, and/or upsell for that article
			ApiService.cancelRequest(AbortAction.MENU_GET_ARTICLE);
			ApiService.cancelRequest(AbortAction.MENU_GET_UPSELL_GROUPS_FOR_ARTICLE);
		},
	});

	// Menu is closed and no pre order is available, totally closed
	const isClosed = menu?.open === false && place?.preOrderEnabled === false;

	const totalAmountExcludingTip = cart && new PriceIncludingVat(cart?.totalAmountExcludingTip);

	// Handle modals that should mount on load when matching sets of conditions
	useEffect(() => {
		switch (true) {
			case isLoading:
			case isCookieModalOpen === undefined:
			case isCookieModalOpen:
			case isClosed:
				// All cases where we want to prompt nothing
				break;
			case !deliveryOptionType:
				// None of the cases above are true and delviery type is missing
				deliveryOptionsModalOnOpen();
				break;
			default:
				break;
		}
	}, [isLoading, isCookieModalOpen, deliveryOptionType, isClosed, deliveryOptionsModalOnOpen]);

	const goToPlaceRoute = () => navigate(`/${appAlias}/${placeId}`);

	const onOpenArticle = (articleId: string, articleName?: string) => {
		if (!menu) return;

		const logString = articleName ? `Name: ${articleName}, Id: ${articleId}` : `Id: ${articleId}`;

		getArticle(menu.id, articleId, cart?.cartId)
			.then((article) => {
				// If the article has upsell, also fetch the upsell-data
				if (article?.hasUpsellGroups)
					getUpsellGroups(menu.id, articleId, cart?.cartId).catch((error) => {
						logError(LogPrefix.Article, error, `Could not get Upsell Groups for Article, ${logString}`);
					});
			})
			.catch((error) => {
				logError(LogPrefix.Article, error, `Could not get Article, ${logString}`);
			});
		// We do not await the article or its upsell groups before opening the article modal,
		// as the loading state of this should be visible in the modal ui
		articleModalOnOpen();
	};

	const cartModalOnOpen = () => {
		setIsCartModalOpen(true);
	};

	const onGoToCartFromUpsell = () => {
		// Open full cart mobile modal
		cartModalOnOpen();
		// Close upsell
		upsellOnClose();
	};

	const onSubmitDeliveryForm = (
		type: DeliveryType | undefined,
		resourceId?: string,
		resourceName?: string,
	) => {
		if (!type || !menu) return;
		const search = createBSTLSearchParams(type, resourceId, Boolean(isShowingMenuSectionList));
		if (cart) {
			const changeRequest: IChangeDeliveryTypeRequest = {
				resourceId,
				type,
			};
			setCartDeliveryType(cart.cartId, changeRequest)
				.then(() => {
					setSearchParams(search);
					logDebug(
						LogPrefix.Cart,
						`Changed delivery from [Type: ${cart.deliveryInfo.type}, Resource: ${cart.deliveryInfo.eatHereNodeResourceName}] to [Type: ${type}, Resource: ${resourceName}]`,
					);
				})
				.catch(() => {
					showToast({
						type: "error",
						description: t("error_generic_reload_page"),
					});
				});
		} else {
			setSearchParams(search);
		}
		deliveryOptionsModalOnClose();
	};

	if (isLoading) {
		return <SkeletonMenu />;
	}

	if (error) {
		let notFoundElement = <NotFound showWithButton />;
		const isErrorThatShouldShowGoBackButton =
			error instanceof BSTLApiError && (error.statusCode === 403 || error.statusCode === 404);

		if (isErrorThatShouldShowGoBackButton) {
			notFoundElement = (
				<NotFound
					showWithButton
					buttonTitle={t("action_back")}
					buttonOnClick={() => {
						navigate(`/${appAlias}`);
					}}
				/>
			);
		}
		return (
			<Box display="flex" h="100%">
				<StyledMenu>{notFoundElement}</StyledMenu>
				<Cart />
			</Box>
		);
	}

	return (
		<>
			<DeliveryOptionsModal
				onSubmit={onSubmitDeliveryForm}
				deliveryTypes={menu?.deliveryTypesSupported}
				isOpen={deliveryOptionsModalIsOpen}
				onClose={deliveryOptionsModalOnClose}
				onCancel={goToPlaceRoute}
				isCloseButtonVisible={false}
			/>
			<ArticleModal isOpen={articleModalIsOpen} onClose={articleModalOnClose} onOpenUpsell={upsellOnOpen} />
			<>
				<Box display="flex">
					<StyledMenu id="menu">
						<>
							<Upsell isOpen={upsellIsOpen} onClose={upsellOnClose} onOpenCart={onGoToCartFromUpsell} />
							{isShowingMenuSectionList && <MenuSectionList />}
							{!isShowingMenuSectionList && !upsellIsOpen && (
								<MenuSectionTabs onClickArticle={onOpenArticle} />
							)}
							{cart && !cart.isEmpty() && (
								<Box
									p={ThemeSpaceVariable.Medium}
									position="sticky"
									bottom="env(safe-area-inset-bottom, 0)"
									display={{ base: undefined, md: "none" }}
								>
									<ShowCartButton onClick={cartModalOnOpen} price={totalAmountExcludingTip} />
								</Box>
							)}
						</>
					</StyledMenu>
					<Cart />
				</Box>
			</>
		</>
	);
}

export default MenuPage;
